import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Building2, ClipboardList, Users, Goal } from 'lucide-react';

const STADIUM_IMAGE = 'https://images.unsplash.com/photo-1676746424139-77f8bd8922a8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwzfHxzb2NjZXIlMjBzdGFkaXVtJTIwbGlnaHRzfGVufDB8fHx8MTc4ODA0NTM0MXww&ixlib=rb-4.1.0&q=85';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const ACCOUNT_TYPES = [
  { key: 'organization', label: 'منشأة / نادي', Icon: Building2, testId: 'register-mode-organization-btn', tint: 'bg-teal-50 text-teal-500', desc: 'مناسب لو تدير نادياً أو أكاديمية رياضية وتحتاج نظاماً متكاملاً لإدارة اللاعبين والمالية والحضور' },
  { key: 'coach', label: 'مدرب مستقل', Icon: ClipboardList, testId: 'register-mode-coach-btn', tint: 'bg-blue-50 text-blue-500', desc: 'مناسب لو تعمل كمدرب مستقل وتريد إدارة برامجك التدريبية ومتابعة لاعبيك بنفسك' },
  { key: 'parent', label: 'ولي أمر', Icon: Users, testId: 'register-mode-parent-btn', tint: 'bg-amber-50 text-amber-500', desc: 'مناسب لو أنت ولي أمر وتريد متابعة أداء وحضور ودفعات أبنائك في النادي' },
  { key: 'venue_owner', label: 'مالك ملعب', Icon: Goal, testId: 'register-mode-venue-owner-btn', tint: 'bg-orange-50 text-orange-500', desc: 'مناسب لو تملك ملعباً أو صالة وتريد استقبال حجوزات اللاعبين والأندية مباشرة' },
];

const emptyOrgForm = { club_name: '', admin_full_name: '', email: '', password: '', phone: '', org_type: '' };
const emptyCoachForm = { full_name: '', username: '', email: '', phone: '', password: '' };
const emptyParentForm = { full_name: '', phone: '', password: '', confirm_password: '' };
const emptyVenueOwnerForm = { club_name: '', admin_full_name: '', email: '', password: '', phone: '' };
const emptyContactForm = { clubName: '', contactPerson: '', email: '', phone: '', city: '', message: '' };

export default function RegisterPage() {
  const initialMode = (() => {
    const type = new URLSearchParams(window.location.search).get('type');
    if (type === 'parent') return 'parent';
    if (type === 'venue_owner' || window.location.pathname === '/venue-owner/register') return 'venue_owner';
    return 'organization';
  })();
  const [mode, setMode] = useState(initialMode); // organization | coach | parent | venue_owner
  const [orgForm, setOrgForm] = useState(emptyOrgForm);
  const [coachForm, setCoachForm] = useState(emptyCoachForm);
  const [parentForm, setParentForm] = useState(emptyParentForm);
  const [venueOwnerForm, setVenueOwnerForm] = useState(emptyVenueOwnerForm);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [platformSettings, setPlatformSettings] = useState({ platform_name: 'تسيار', platform_logo: null });

  const [showContactModal, setShowContactModal] = useState(false);
  const [contactForm, setContactForm] = useState(emptyContactForm);
  const [contactLoading, setContactLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'إنشاء حساب - تسيار';
    axios.get(`${API}/api/platform/settings`).then((res) => {
      if (res.data) setPlatformSettings(res.data);
    }).catch(() => {});
  }, []);

  const submitOrganization = async (e) => {
    e.preventDefault();
    if (!agreedToTerms) { setError('يجب الموافقة على الشروط والأحكام وسياسة الخصوصية للاستمرار'); return; }
    if (!orgForm.org_type) { setError('يرجى اختيار نوع المنشأة'); return; }
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/saas/register-organization`, orgForm);
      localStorage.setItem('token', res.data.access_token);
      localStorage.setItem('user', JSON.stringify(res.data.user_info));
      axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.access_token}`;
      window.location.href = '/dashboard';
    } catch (err) {
      setError(err?.response?.data?.detail || 'فشل إنشاء المنشأة، تحقق من البيانات المدخلة');
      setLoading(false);
    }
  };

  const submitCoach = async (e) => {
    e.preventDefault();
    if (!agreedToTerms) { setError('يجب الموافقة على الشروط والأحكام وسياسة الخصوصية للاستمرار'); return; }
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/coaches/register`, coachForm);
      localStorage.setItem('token', res.data.access_token);
      localStorage.setItem('user', JSON.stringify(res.data.user_info));
      axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.access_token}`;
      window.location.href = '/coach/profile';
    } catch (err) {
      setError(err?.response?.data?.detail || 'فشل إنشاء الحساب، تحقق من البيانات المدخلة');
      setLoading(false);
    }
  };

  const submitParent = async (e) => {
    e.preventDefault();
    if (!agreedToTerms) { setError('يجب الموافقة على الشروط والأحكام وسياسة الخصوصية للاستمرار'); return; }
    if (parentForm.password !== parentForm.confirm_password) { setError('كلمتا المرور غير متطابقتين'); return; }
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/parent/register`, parentForm);
      localStorage.setItem('token', res.data.access_token);
      localStorage.setItem('user', JSON.stringify(res.data.user_info));
      axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.access_token}`;
      window.location.href = '/parent';
    } catch (err) {
      setError(err?.response?.data?.detail || 'فشل إنشاء الحساب، تحقق من البيانات المدخلة');
      setLoading(false);
    }
  };

  const submitVenueOwner = async (e) => {
    e.preventDefault();
    if (!agreedToTerms) { setError('يجب الموافقة على الشروط والأحكام وسياسة الخصوصية للاستمرار'); return; }
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/saas/register-venue-owner`, venueOwnerForm);
      localStorage.setItem('token', res.data.access_token);
      localStorage.setItem('user', JSON.stringify(res.data.user_info));
      axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.access_token}`;
      window.location.href = '/venue-owner-dashboard';
    } catch (err) {
      setError(err?.response?.data?.detail || 'فشل إنشاء الحساب، تحقق من البيانات المدخلة');
      setLoading(false);
    }
  };

  const submitContactRequest = async (e) => {
    e.preventDefault();
    if (!contactForm.clubName || !contactForm.contactPerson || !contactForm.email) {
      alert('يرجى ملء الحقول المطلوبة');
      return;
    }
    setContactLoading(true);
    try {
      await axios.post(`${API}/api/saas/registration-requests`, { ...contactForm, submitted_at: new Date().toISOString(), status: 'pending' });
      alert('تم إرسال طلبك بنجاح! سيتم التواصل معك قريباً.');
      setShowContactModal(false);
      setContactForm(emptyContactForm);
    } catch (err) {
      alert('حدث خطأ في إرسال الطلب. يرجى المحاولة مرة أخرى.');
    } finally {
      setContactLoading(false);
    }
  };

  return (
    <div dir="rtl" className="min-h-screen flex flex-col lg:flex-row" style={{ fontFamily: "'Tajawal', 'Cairo', sans-serif" }} data-testid="register-page">
      {/* Left Visual Panel */}
      <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden bg-[#0A192F]">
        <img src={STADIUM_IMAGE} alt="ملعب رياضي" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#0A192F]/95 via-[#0A192F]/80 to-[#0D9488]/40" />
        <div
          className="absolute inset-0 opacity-[0.15]"
          style={{ backgroundImage: 'linear-gradient(to right,#ffffff22 1px,transparent 1px),linear-gradient(to bottom,#ffffff22 1px,transparent 1px)', backgroundSize: '32px 32px' }}
        />
        <div className="relative z-10 flex flex-col justify-between p-14 text-white">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors w-fit" data-testid="register-back-home-link">
            <span>→</span> العودة للرئيسية
          </Link>

          <div className="animate-[fadeUp_0.7s_ease-out]">
            <span className="inline-block rounded-full bg-orange-500/15 border border-orange-400/30 px-4 py-1.5 text-xs font-bold text-orange-300 tracking-wide">
              ابدأ تجربتك المجانية لمدة 30 يوماً
            </span>
            <h2 className="mt-6 font-black text-4xl leading-tight tracking-tight">
              انضم إلى تسيار،<br />وابدأ فوراً
            </h2>
            <p className="mt-4 max-w-md text-white/60 text-base leading-relaxed">
              سجّل منشأتك الرياضية وابدأ العمل فوراً بلا انتظار، أو انضم كمدرب مستقل واعرض ملفك المهني لعشرات الأندية.
            </p>
          </div>

          <div className="flex items-center gap-8 text-white/50 text-xs">
            <span>© {new Date().getFullYear()} تسيار</span>
            <span className="h-1 w-1 rounded-full bg-white/30" />
            <span>جميع الحقوق محفوظة</span>
          </div>
        </div>
      </div>

      {/* Right Form Panel */}
      <div
        className="flex-1 flex items-center justify-center bg-white relative px-6 sm:px-16 lg:px-24 py-14"
        style={{ backgroundImage: 'linear-gradient(to right,#80808012 1px,transparent 1px),linear-gradient(to bottom,#80808012 1px,transparent 1px)', backgroundSize: '24px 24px' }}
      >
        <div className="lg:hidden absolute top-6 right-6">
          <Link to="/" className="text-sm font-medium text-slate-500 hover:text-teal-600 transition-colors">→ الرئيسية</Link>
        </div>

        <div className="w-full max-w-sm">
          {platformSettings.platform_logo ? (
            <img src={`${API}/api/files/${platformSettings.platform_logo}`} alt={platformSettings.platform_name} className="h-16 object-contain" data-testid="register-platform-logo" />
          ) : (
            <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-[#0A192F] to-[#0D9488] flex items-center justify-center text-xl font-black text-white">
              {(platformSettings.platform_name || 'تسيار').charAt(0)}
            </div>
          )}

          <h1 className="mt-6 font-black text-2xl tracking-tight text-[#0A192F]">إنشاء حساب جديد</h1>
          <p className="mt-2 text-slate-500 text-sm">اختر نوع الحساب المناسب لك، وابدأ في دقيقة واحدة</p>

          {/* Mode Switch */}
          <div className="mt-6 grid grid-cols-2 gap-2.5" data-testid="register-mode-switch">
            {ACCOUNT_TYPES.map(({ key, label, Icon, testId, tint }) => {
              const active = mode === key;
              const accent = key === 'venue_owner' ? 'orange' : 'teal';
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => { setMode(key); setError(''); }}
                  data-testid={testId}
                  className={`group flex flex-col items-center gap-2 rounded-2xl border-2 p-3.5 transition-all sm:p-4 ${
                    active
                      ? accent === 'orange'
                        ? 'border-orange-400 bg-orange-50 shadow-[0_6px_18px_-8px_rgba(249,115,22,0.5)]'
                        : 'border-teal-500 bg-teal-50 shadow-[0_6px_18px_-8px_rgba(13,148,136,0.5)]'
                      : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  <span className={`flex h-11 w-11 items-center justify-center rounded-full transition-colors ${
                    active
                      ? accent === 'orange' ? 'bg-orange-500 text-white' : 'bg-teal-600 text-white'
                      : tint
                  }`}>
                    <Icon className="h-5 w-5" strokeWidth={2.2} />
                  </span>
                  <span className={`text-xs font-bold sm:text-sm ${active ? (accent === 'orange' ? 'text-orange-700' : 'text-teal-700') : 'text-slate-500'}`}>
                    {label}
                  </span>
                </button>
              );
            })}
          </div>

          {ACCOUNT_TYPES.filter((t) => t.key === mode).map((t) => (
            <p key={t.key} className="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-center text-xs text-slate-500 sm:text-sm" data-testid="register-mode-description">
              {t.desc}
            </p>
          ))}

          {error && (
            <div className="mt-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600" data-testid="register-error-message">
              {error}
            </div>
          )}

          {mode === 'organization' ? (
            <form onSubmit={submitOrganization} className="mt-6 space-y-4" data-testid="register-organization-form">
              <div>
                <label className="mb-1.5 block text-xs font-bold text-slate-600">اسم المنشأة</label>
                <input required data-testid="register-org-club-name" value={orgForm.club_name} onChange={(e) => setOrgForm({ ...orgForm, club_name: e.target.value })}
                  className="h-14 w-full rounded-xl bg-slate-50 px-4 text-slate-800 outline-none border border-transparent transition-all focus:ring-2 focus:ring-teal-600 focus:bg-white" placeholder="مثال: أكاديمية النخبة الرياضية" />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-bold text-slate-600">نوع المنشأة</label>
                <select required data-testid="register-org-type" value={orgForm.org_type} onChange={(e) => setOrgForm({ ...orgForm, org_type: e.target.value })}
                  className="h-14 w-full rounded-xl bg-slate-50 px-4 text-slate-800 outline-none border border-transparent transition-all focus:ring-2 focus:ring-teal-600 focus:bg-white">
                  <option value="" disabled>اختر نوع المنشأة</option>
                  <option value="club">نادي رياضي</option>
                  <option value="academy">أكاديمية رياضية</option>
                  <option value="both">نادي وأكاديمية (كلاهما)</option>
                </select>
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-bold text-slate-600">اسم المدير</label>
                <input required data-testid="register-org-admin-name" value={orgForm.admin_full_name} onChange={(e) => setOrgForm({ ...orgForm, admin_full_name: e.target.value })}
                  className="h-14 w-full rounded-xl bg-slate-50 px-4 text-slate-800 outline-none border border-transparent transition-all focus:ring-2 focus:ring-teal-600 focus:bg-white" placeholder="الاسم الكامل" />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-bold text-slate-600">البريد الإلكتروني</label>
                <input required type="email" data-testid="register-org-email" value={orgForm.email} onChange={(e) => setOrgForm({ ...orgForm, email: e.target.value })}
                  className="h-14 w-full rounded-xl bg-slate-50 px-4 text-slate-800 outline-none border border-transparent transition-all focus:ring-2 focus:ring-teal-600 focus:bg-white" placeholder="admin@example.com" />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-bold text-slate-600">رقم الجوال (اختياري)</label>
                <input data-testid="register-org-phone" value={orgForm.phone} onChange={(e) => setOrgForm({ ...orgForm, phone: e.target.value })}
                  className="h-14 w-full rounded-xl bg-slate-50 px-4 text-slate-800 outline-none border border-transparent transition-all focus:ring-2 focus:ring-teal-600 focus:bg-white" placeholder="05xxxxxxxx" />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-bold text-slate-600">كلمة المرور</label>
                <div className="relative">
                  <input required minLength={6} type={showPassword ? 'text' : 'password'} data-testid="register-org-password" value={orgForm.password} onChange={(e) => setOrgForm({ ...orgForm, password: e.target.value })}
                    className="h-14 w-full rounded-xl bg-slate-50 px-4 pl-12 text-slate-800 outline-none border border-transparent transition-all focus:ring-2 focus:ring-teal-600 focus:bg-white" placeholder="6 أحرف على الأقل" />
                  <button type="button" onClick={() => setShowPassword((s) => !s)} className="absolute left-4 top-1/2 -translate-y-1/2 text-xs font-semibold text-slate-400 hover:text-teal-600">
                    {showPassword ? 'إخفاء' : 'عرض'}
                  </button>
                </div>
              </div>
              <label className="flex items-start gap-2.5 text-xs text-slate-500" data-testid="register-org-terms-label">
                <input type="checkbox" checked={agreedToTerms} onChange={(e) => setAgreedToTerms(e.target.checked)} data-testid="register-org-terms-checkbox"
                  className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-teal-600 focus:ring-teal-500" />
                <span>أوافق على <a href="/terms" target="_blank" rel="noreferrer" className="font-semibold text-teal-600 hover:underline">الشروط والأحكام</a> و<a href="/privacy" target="_blank" rel="noreferrer" className="font-semibold text-teal-600 hover:underline">سياسة الخصوصية</a></span>
              </label>
              <button type="submit" disabled={loading || !agreedToTerms} data-testid="register-org-submit-btn"
                className="h-14 w-full rounded-xl bg-teal-600 text-lg font-bold text-white transition-all hover:-translate-y-0.5 hover:shadow-[0_8px_20px_-6px_rgba(13,148,136,0.5)] disabled:opacity-50 disabled:translate-y-0 disabled:shadow-none">
                {loading ? 'جارٍ إنشاء المنشأة...' : 'ابدأ تجربتك المجانية الآن'}
              </button>
              <p className="text-center text-xs text-slate-400">تجربة مجانية 30 يوماً، بدون بطاقة بنكية، ودون انتظار موافقة</p>
            </form>
          ) : mode === 'coach' ? (
            <form onSubmit={submitCoach} className="mt-6 space-y-4" data-testid="register-coach-form">
              <div>
                <label className="mb-1.5 block text-xs font-bold text-slate-600">الاسم الكامل</label>
                <input required data-testid="register-coach-fullname" value={coachForm.full_name} onChange={(e) => setCoachForm({ ...coachForm, full_name: e.target.value })}
                  className="h-14 w-full rounded-xl bg-slate-50 px-4 text-slate-800 outline-none border border-transparent transition-all focus:ring-2 focus:ring-teal-600 focus:bg-white" />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-bold text-slate-600">اسم المستخدم</label>
                <input required data-testid="register-coach-username" value={coachForm.username} onChange={(e) => setCoachForm({ ...coachForm, username: e.target.value })}
                  className="h-14 w-full rounded-xl bg-slate-50 px-4 text-slate-800 outline-none border border-transparent transition-all focus:ring-2 focus:ring-teal-600 focus:bg-white" />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-bold text-slate-600">البريد الإلكتروني</label>
                <input type="email" data-testid="register-coach-email" value={coachForm.email} onChange={(e) => setCoachForm({ ...coachForm, email: e.target.value })}
                  className="h-14 w-full rounded-xl bg-slate-50 px-4 text-slate-800 outline-none border border-transparent transition-all focus:ring-2 focus:ring-teal-600 focus:bg-white" />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-bold text-slate-600">رقم الجوال</label>
                <input data-testid="register-coach-phone" value={coachForm.phone} onChange={(e) => setCoachForm({ ...coachForm, phone: e.target.value })}
                  className="h-14 w-full rounded-xl bg-slate-50 px-4 text-slate-800 outline-none border border-transparent transition-all focus:ring-2 focus:ring-teal-600 focus:bg-white" />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-bold text-slate-600">كلمة المرور</label>
                <div className="relative">
                  <input required minLength={6} type={showPassword ? 'text' : 'password'} data-testid="register-coach-password" value={coachForm.password} onChange={(e) => setCoachForm({ ...coachForm, password: e.target.value })}
                    className="h-14 w-full rounded-xl bg-slate-50 px-4 pl-12 text-slate-800 outline-none border border-transparent transition-all focus:ring-2 focus:ring-teal-600 focus:bg-white" placeholder="6 أحرف على الأقل" />
                  <button type="button" onClick={() => setShowPassword((s) => !s)} className="absolute left-4 top-1/2 -translate-y-1/2 text-xs font-semibold text-slate-400 hover:text-teal-600">
                    {showPassword ? 'إخفاء' : 'عرض'}
                  </button>
                </div>
              </div>
              <label className="flex items-start gap-2.5 text-xs text-slate-500" data-testid="register-coach-terms-label">
                <input type="checkbox" checked={agreedToTerms} onChange={(e) => setAgreedToTerms(e.target.checked)} data-testid="register-coach-terms-checkbox"
                  className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-teal-600 focus:ring-teal-500" />
                <span>أوافق على <a href="/terms" target="_blank" rel="noreferrer" className="font-semibold text-teal-600 hover:underline">الشروط والأحكام</a> و<a href="/privacy" target="_blank" rel="noreferrer" className="font-semibold text-teal-600 hover:underline">سياسة الخصوصية</a></span>
              </label>
              <button type="submit" disabled={loading || !agreedToTerms} data-testid="register-coach-submit-btn"
                className="h-14 w-full rounded-xl bg-teal-600 text-lg font-bold text-white transition-all hover:-translate-y-0.5 hover:shadow-[0_8px_20px_-6px_rgba(13,148,136,0.5)] disabled:opacity-50 disabled:translate-y-0 disabled:shadow-none">
                {loading ? 'جارِ إنشاء الحساب...' : 'إنشاء حساب المدرب'}
              </button>
            </form>
          ) : mode === 'parent' ? (
            <form onSubmit={submitParent} className="mt-6 space-y-4" data-testid="register-parent-form">
              <div>
                <label className="mb-1.5 block text-xs font-bold text-slate-600">الاسم الكامل</label>
                <input required data-testid="register-parent-fullname" value={parentForm.full_name} onChange={(e) => setParentForm({ ...parentForm, full_name: e.target.value })}
                  className="h-14 w-full rounded-xl bg-slate-50 px-4 text-slate-800 outline-none border border-transparent transition-all focus:ring-2 focus:ring-teal-600 focus:bg-white" />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-bold text-slate-600">رقم الجوال السعودي</label>
                <input required dir="ltr" data-testid="register-parent-phone" value={parentForm.phone} onChange={(e) => setParentForm({ ...parentForm, phone: e.target.value })}
                  className="h-14 w-full rounded-xl bg-slate-50 px-4 text-slate-800 outline-none border border-transparent transition-all focus:ring-2 focus:ring-teal-600 focus:bg-white" placeholder="05xxxxxxxx" />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-bold text-slate-600">كلمة المرور</label>
                <div className="relative">
                  <input required minLength={6} type={showPassword ? 'text' : 'password'} data-testid="register-parent-password" value={parentForm.password} onChange={(e) => setParentForm({ ...parentForm, password: e.target.value })}
                    className="h-14 w-full rounded-xl bg-slate-50 px-4 pl-12 text-slate-800 outline-none border border-transparent transition-all focus:ring-2 focus:ring-teal-600 focus:bg-white" placeholder="6 أحرف على الأقل" />
                  <button type="button" onClick={() => setShowPassword((s) => !s)} className="absolute left-4 top-1/2 -translate-y-1/2 text-xs font-semibold text-slate-400 hover:text-teal-600">
                    {showPassword ? 'إخفاء' : 'عرض'}
                  </button>
                </div>
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-bold text-slate-600">تأكيد كلمة المرور</label>
                <input required minLength={6} type={showPassword ? 'text' : 'password'} data-testid="register-parent-confirm-password" value={parentForm.confirm_password} onChange={(e) => setParentForm({ ...parentForm, confirm_password: e.target.value })}
                  className="h-14 w-full rounded-xl bg-slate-50 px-4 text-slate-800 outline-none border border-transparent transition-all focus:ring-2 focus:ring-teal-600 focus:bg-white" placeholder="أعد كتابة كلمة المرور" />
              </div>
              <label className="flex items-start gap-2.5 text-xs text-slate-500" data-testid="register-parent-terms-label">
                <input type="checkbox" checked={agreedToTerms} onChange={(e) => setAgreedToTerms(e.target.checked)} data-testid="register-parent-terms-checkbox"
                  className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-teal-600 focus:ring-teal-500" />
                <span>أوافق على <a href="/terms" target="_blank" rel="noreferrer" className="font-semibold text-teal-600 hover:underline">الشروط والأحكام</a> و<a href="/privacy" target="_blank" rel="noreferrer" className="font-semibold text-teal-600 hover:underline">سياسة الخصوصية</a></span>
              </label>
              <button type="submit" disabled={loading || !agreedToTerms} data-testid="register-parent-submit-btn"
                className="h-14 w-full rounded-xl bg-teal-600 text-lg font-bold text-white transition-all hover:-translate-y-0.5 hover:shadow-[0_8px_20px_-6px_rgba(13,148,136,0.5)] disabled:opacity-50 disabled:translate-y-0 disabled:shadow-none">
                {loading ? 'جارٍ إنشاء الحساب...' : 'إنشاء حساب ولي الأمر'}
              </button>
              <p className="text-center text-xs text-slate-400">سيبحث النظام تلقائياً عن أبنائك المسجلين في الأكاديميات برقم جوالك، وترسل طلبات الربط للموافقة</p>
            </form>
          ) : (
            <form onSubmit={submitVenueOwner} className="mt-6 space-y-4" data-testid="register-venue-owner-form">
              <div className="rounded-xl bg-orange-50 border border-orange-100 px-4 py-3 text-xs text-orange-700">
                لوحة مستقلة وسريعة لإدارة ملاعبك وحجوزاتك — <b>تسيار للملاعب</b>. تجربة مجانية 30 يوماً بلا انتظار موافقة.
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-bold text-slate-600">اسم الملعب / النشاط</label>
                <input required data-testid="register-venue-owner-name" value={venueOwnerForm.club_name} onChange={(e) => setVenueOwnerForm({ ...venueOwnerForm, club_name: e.target.value })}
                  className="h-14 w-full rounded-xl bg-slate-50 px-4 text-slate-800 outline-none border border-transparent transition-all focus:ring-2 focus:ring-orange-500 focus:bg-white" placeholder="مثال: ملاعب الواحة الرياضية" />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-bold text-slate-600">اسم المالك</label>
                <input required data-testid="register-venue-owner-admin-name" value={venueOwnerForm.admin_full_name} onChange={(e) => setVenueOwnerForm({ ...venueOwnerForm, admin_full_name: e.target.value })}
                  className="h-14 w-full rounded-xl bg-slate-50 px-4 text-slate-800 outline-none border border-transparent transition-all focus:ring-2 focus:ring-orange-500 focus:bg-white" placeholder="الاسم الكامل" />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-bold text-slate-600">البريد الإلكتروني</label>
                <input required type="email" data-testid="register-venue-owner-email" value={venueOwnerForm.email} onChange={(e) => setVenueOwnerForm({ ...venueOwnerForm, email: e.target.value })}
                  className="h-14 w-full rounded-xl bg-slate-50 px-4 text-slate-800 outline-none border border-transparent transition-all focus:ring-2 focus:ring-orange-500 focus:bg-white" placeholder="owner@example.com" />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-bold text-slate-600">رقم الجوال (واتساب)</label>
                <input data-testid="register-venue-owner-phone" value={venueOwnerForm.phone} onChange={(e) => setVenueOwnerForm({ ...venueOwnerForm, phone: e.target.value })}
                  className="h-14 w-full rounded-xl bg-slate-50 px-4 text-slate-800 outline-none border border-transparent transition-all focus:ring-2 focus:ring-orange-500 focus:bg-white" placeholder="05xxxxxxxx" />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-bold text-slate-600">كلمة المرور</label>
                <div className="relative">
                  <input required minLength={6} type={showPassword ? 'text' : 'password'} data-testid="register-venue-owner-password" value={venueOwnerForm.password} onChange={(e) => setVenueOwnerForm({ ...venueOwnerForm, password: e.target.value })}
                    className="h-14 w-full rounded-xl bg-slate-50 px-4 pl-12 text-slate-800 outline-none border border-transparent transition-all focus:ring-2 focus:ring-orange-500 focus:bg-white" placeholder="6 أحرف على الأقل" />
                  <button type="button" onClick={() => setShowPassword((s) => !s)} className="absolute left-4 top-1/2 -translate-y-1/2 text-xs font-semibold text-slate-400 hover:text-orange-600">
                    {showPassword ? 'إخفاء' : 'عرض'}
                  </button>
                </div>
              </div>
              <label className="flex items-start gap-2.5 text-xs text-slate-500" data-testid="register-venue-owner-terms-label">
                <input type="checkbox" checked={agreedToTerms} onChange={(e) => setAgreedToTerms(e.target.checked)} data-testid="register-venue-owner-terms-checkbox"
                  className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-orange-600 focus:ring-orange-500" />
                <span>أوافق على <a href="/terms" target="_blank" rel="noreferrer" className="font-semibold text-orange-600 hover:underline">الشروط والأحكام</a> و<a href="/privacy" target="_blank" rel="noreferrer" className="font-semibold text-orange-600 hover:underline">سياسة الخصوصية</a></span>
              </label>
              <button type="submit" disabled={loading || !agreedToTerms} data-testid="register-venue-owner-submit-btn"
                className="h-14 w-full rounded-xl bg-orange-600 text-lg font-bold text-white transition-all hover:-translate-y-0.5 hover:shadow-[0_8px_20px_-6px_rgba(234,88,12,0.5)] disabled:opacity-50 disabled:translate-y-0 disabled:shadow-none">
                {loading ? 'جارٍ إنشاء حسابك...' : 'ابدأ تجربتك المجانية الآن'}
              </button>
              <p className="text-center text-xs text-slate-400">نشر ملعبك في السوق العام لتسيار يحتاج اعتماد فريقنا، لكن لوحتك جاهزة للاستخدام فوراً</p>
            </form>
          )}

          <div className="mt-6 flex items-center justify-between border-t border-slate-100 pt-5 text-sm">
            <Link to="/login" data-testid="register-goto-login-link" className="font-semibold text-slate-500 hover:text-teal-600 hover:underline transition-colors">
              لديك حساب؟ سجّل دخولك
            </Link>
            <button type="button" onClick={() => setShowContactModal(true)} data-testid="register-contact-instead-link" className="font-semibold text-slate-500 hover:text-teal-600 hover:underline transition-colors">
              تفضل التواصل معنا بدلاً من ذلك؟
            </button>
          </div>
        </div>
      </div>

      {showContactModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="register-contact-modal">
          <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-2xl">
            <h3 className="text-xl font-bold text-[#0A192F]">طلب تسجيل تقليدي</h3>
            <p className="mt-1 text-sm text-slate-500">سيراجع فريقنا طلبك ويتواصل معك خلال 24-48 ساعة</p>
            <form onSubmit={submitContactRequest} className="mt-5 space-y-3">
              <input required data-testid="register-contact-club-name" placeholder="اسم النادي *" value={contactForm.clubName} onChange={(e) => setContactForm({ ...contactForm, clubName: e.target.value })}
                className="h-12 w-full rounded-lg border border-slate-200 px-4 text-sm outline-none focus:ring-2 focus:ring-teal-500" />
              <input required data-testid="register-contact-person" placeholder="اسم الشخص المسؤول *" value={contactForm.contactPerson} onChange={(e) => setContactForm({ ...contactForm, contactPerson: e.target.value })}
                className="h-12 w-full rounded-lg border border-slate-200 px-4 text-sm outline-none focus:ring-2 focus:ring-teal-500" />
              <input required type="email" data-testid="register-contact-email" placeholder="البريد الإلكتروني *" value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                className="h-12 w-full rounded-lg border border-slate-200 px-4 text-sm outline-none focus:ring-2 focus:ring-teal-500" />
              <input data-testid="register-contact-phone" placeholder="رقم الهاتف" value={contactForm.phone} onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                className="h-12 w-full rounded-lg border border-slate-200 px-4 text-sm outline-none focus:ring-2 focus:ring-teal-500" />
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowContactModal(false)} data-testid="register-contact-cancel-btn" className="flex-1 rounded-lg bg-slate-100 py-3 text-sm font-bold text-slate-600 hover:bg-slate-200 transition-colors">إلغاء</button>
                <button type="submit" disabled={contactLoading} data-testid="register-contact-submit-btn" className="flex-1 rounded-lg bg-teal-600 py-3 text-sm font-bold text-white hover:bg-teal-700 transition-colors disabled:opacity-50">
                  {contactLoading ? 'جارٍ الإرسال...' : 'إرسال الطلب'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
